import React from "react"
import { useNavigate } from "react-router-dom"
import { useCart } from "@/hooks/useCart"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Trash2, Plus, Minus, ShoppingCart } from "lucide-react"
import { apiClient } from "@/api/client"
import { useLanguage } from "@/hooks/useLanguage"
import {
  formatCurrency,
  formatPricePerWeightUnit,
  formatTotalWeight,
  formatUnitsBreakdown,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"

interface CartSheetProps {
  isOpen: boolean
  onClose: () => void
  /** @deprecated Language is now managed by LanguageContext. */
  language?: "es" | "en"
}

const CartSheet: React.FC<CartSheetProps> = ({ isOpen, onClose }) => {
  const { items, removeItem, updateQuantity, totalPrice, totalItems, clearCart } = useCart()
  const navigate = useNavigate()
  const { t, language } = useLanguage()

  const text = t.cartSheet

  return (
    <Sheet open={isOpen} onOpenChange={onClose} modal={false}>
      <SheetContent
        className="flex w-full flex-col border-l-[1px] border-border bg-background sm:max-w-md"
        onPointerDownOutside={(e) => {
          // If the click is on a toast, don't close the sheet
          if (
            (e.target as HTMLElement)?.closest("[data-radix-toast-root]") ||
            (e.target as HTMLElement)?.closest("[data-sonner-toast]")
          ) {
            e.preventDefault()
          } else {
            onClose()
          }
        }}
      >
        <SheetHeader className="border-b-[1px] border-border pb-6">
          <SheetTitle className="flex items-center gap-3 font-display text-2xl uppercase tracking-widest">
            <ShoppingCart className="h-6 w-6 text-primary" />
            {text.title}{" "}
            <span className="text-muted-foreground opacity-50">
              ({totalItems} {text.items})
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center space-y-4">
            <ShoppingCart className="h-16 w-16 text-muted-foreground opacity-20" />
            <p className="text-muted-foreground">{text.empty}</p>
            <Button
              variant="outline"
              onClick={() => {
                navigate("/catalog/api")
                onClose()
              }}
            >
              {text.continueShopping}
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="-mx-6 flex-1 px-6">
              <div className="space-y-6 py-6">
                {items.map((item) => {
                  const imageUrl = apiClient.ensureAbsUrl(item.primaryImageUrl)
                  return (
                    <div key={item.id} className="group flex gap-4">
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border-[1px] border-border bg-muted/30">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.name}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <ShoppingCart className="h-8 w-8 text-muted-foreground/20" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between">
                            <h4 className="line-clamp-2 font-display text-xl uppercase tracking-wider text-foreground">
                              {item.name}
                            </h4>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => item.id !== undefined && removeItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                            {formatUnitsBreakdown(item.quantity, item.weight, item.weightUnit, t.units)}
                          </p>
                          {item.weight && item.weight > 0 && (
                            <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                              {formatTotalWeight(item.quantity, item.weight, item.weightUnit, t.units.total)}
                              {" · "}
                              {formatPricePerWeightUnit(
                                getPricePerWeightUnit(item),
                                item.weightUnit,
                                { withTax: !!item.isTaxable, isTaxable: !!item.isTaxable }
                              )}
                            </p>
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center overflow-hidden rounded-lg border-[1px] border-border bg-background">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-none transition-colors hover:bg-accent/5 hover:text-accent"
                              onClick={() =>
                                item.id !== undefined && updateQuantity(item.id, item.quantity - 1)
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 border-x border-border text-center text-xs font-medium">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-none transition-colors hover:bg-accent/5 hover:text-accent"
                              onClick={() =>
                                item.id !== undefined && updateQuantity(item.id, item.quantity + 1)
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="font-display text-2xl tracking-widest text-foreground">
                            {formatCurrency((item.price || 0) * item.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </ScrollArea>

            <div className="space-y-6 border-t-[1px] border-border pt-6">
              <div className="flex items-end justify-between">
                <span className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
                  {text.subtotal}
                </span>
                <span className="font-display text-4xl leading-none tracking-widest text-foreground">
                  {formatCurrency(totalPrice)}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <Button
                  className="btn-industrial h-14 w-full bg-action shadow-xl transition-all hover:brightness-110 active:scale-95"
                  onClick={() => {
                    navigate("/checkout")
                    onClose()
                  }}
                >
                  {text.checkout}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full rounded-none text-xs font-black uppercase tracking-widest text-muted-foreground transition-colors hover:bg-transparent hover:text-destructive"
                  onClick={clearCart}
                >
                  [ {text.clear} ]
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default CartSheet

